import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import * as searchApi from '../api/searchApi';
import type { OmniboxResult } from '../model/types';

export const searchKeys = {
  all: ['search'] as const,
  omnibox: (query: string) => ['search', 'omnibox', query] as const,
};

/**
 * Odczekanie między naciśnięciem klawisza a zapytaniem.
 *
 * 120 ms, nie 300: omnibox ma **prowadzić przy pisaniu** (spec 5.1 — „parsowanie na żywo,
 * podświetlanie rozpoznanych encji"), więc panel nie może zostawać wyraźnie w tyle za polem.
 * Przy krótszym odczekaniu każda litera to osobne żądanie, przy dłuższym widać opóźnienie.
 */
const DEBOUNCE_MS = 120;

function useDebounced<T>(value: T, delay = DEBOUNCE_MS): T {
  const [odroczona, setOdroczona] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setOdroczona(value), delay);

    return () => clearTimeout(timer);
  }, [value, delay]);

  return odroczona;
}

/**
 * Jedno zapytanie omniboksa: **rozbiór komendy i trafienia naraz** (spec sek. 5).
 *
 * ⚠ `keepPreviousData` nie jest tu kosmetyką — bez niego lista trafień **znika przy każdej
 * literze** i zamiast zawężać się w miejscu, mruga. Ta sama mina, która wyszła na drzewku
 * przy plakietkach właścicieli, tyle że tutaj widać ją od pierwszego użycia.
 */
export function useOmnibox(input: string, enabled: boolean) {
  const debounced = useDebounced(input);

  const query = useQuery<OmniboxResult>({
    queryKey: searchKeys.omnibox(debounced),
    queryFn: ({ signal }) => searchApi.fetchOmnibox(debounced, signal),
    enabled,
    placeholderData: keepPreviousData,
    // Wyniki szukania starzeją się szybko, ale nie w ciągu jednego otwarcia pola —
    // a powrót do tej samej frazy przy poprawianiu literówki jest bardzo częsty.
    staleTime: 30_000,
  });

  return {
    ...query,
    /** Pole wyprzedza odpowiedź — panel ma to pokazać, zamiast udawać, że jest gotowy. */
    isStale: debounced !== input,
  };
}
